import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SessionService } from '../data-access/session.service';

/**
 * Intercepts and modifies HTTP requests within the application.
 * This interceptor sets the 'environment' header to 'sandbox' for all outgoing HTTP requests
 * and modifies the base URL to prepend the environment's API URL.
 *
 * Typically used within an Angular module by providing it in the module's providers array.
 *
 * Usage:
 * @NgModule({
 *   ...
 *   providers: [
 *     { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true },
 *   ],
 *   ...
 * })
 * export class AppModule {}
 *
 * @param {Router} router - The Angular Router instance, injected to allow URL checks.
 */
@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
  public router = inject(Router);
  public sessionService = inject(SessionService);
  /**
   * Intercepts HTTP requests to add authentication and environment headers.
   *
   * - Prepends the base API URL to the request URL.
   * - Retrieves the patron's token and environment from the session service.
   * - If no token is found in session storage or the patron object, throws an error (except for 'identify' requests).
   * - Clones the original request and appends the `Authorization` and `environment` headers.
   *
   *
   * @throws {Error} If no token is present and the request URL doesn't include 'identify'.
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = `${environment.apiUrl}${request.url}`;
    const patron = this.sessionService.getPatron()!;
    const orgEnvironment = patron?.environment;
    let token: string | null = sessionStorage.getItem('jwt');

    if (!token) {
      token = patron?.token;
    }

    if (!request.url.includes('identify') && token == null) {
      throw new Error('Token is not present. If you see this error there is some edge case to handle still.');
    }

    const modifiedRequest = request.clone({
      url,
      setHeaders: {
        environment: orgEnvironment ?? 'default',
        Authorization: `Bearer ${token}`,
      },
    });

    return next.handle(modifiedRequest).pipe(
      map(event => {
        if (event instanceof HttpResponse && environment.debugApiResponse) {
          console.log(`Response from ${event.url} with body:`, event.body);
        }

        return event;
      })
    );
  }
}
